import { useMemo } from "react";
import { Card, Empty, Typography } from "antd";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { WORKFLOW_STATUSES } from "../../lib/projectStatus";

interface StageFunnelCardProps {
  funnel: { status: string; count: number }[];
  totalShipped?: number;
}

const PRIMARY = "#092940";
const ACCENT = "#1E79C8";

export default function StageFunnelCard({ funnel, totalShipped }: StageFunnelCardProps) {
  const navigate = useNavigate();
  const data = useMemo(() => {
    const byStatus = new Map(funnel.map((row) => [row.status, row.count]));
    return WORKFLOW_STATUSES.map((status) => ({
      status,
      // Keep the labels short on the bar's category axis; full name is in tooltip.
      short: status
        .replace("Initial Intake Conversation Complete", "Intake convo")
        .replace("Evaluation Template Complete", "Eval template")
        .replace("AI Advisory Board Approved", "Advisory")
        .replace("AI Steer Co Approved", "Steer Co")
        .replace("Had Scoping Call", "Scoping"),
      count: byStatus.get(status) || 0,
    }));
  }, [funnel]);

  const total = data.reduce((acc, row) => acc + row.count, 0);

  return (
    <Card
      title="Pipeline by stage"
      extra={
        totalShipped != null ? (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {totalShipped} delivered in window
          </Typography.Text>
        ) : null
      }
    >
      {total === 0 ? (
        <Empty description="No active projects in this period" />
      ) : (
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 12, right: 12, left: 0, bottom: 8 }}
              aria-label="Bar chart of active projects by workflow stage"
            >
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(9,41,64,0.08)" />
              <XAxis
                dataKey="short"
                tick={{ fontSize: 11, fill: PRIMARY }}
                interval={0}
                angle={-20}
                textAnchor="end"
                height={60}
              />
              <YAxis
                allowDecimals={false}
                tick={{ fontSize: 12, fill: PRIMARY }}
                width={32}
              />
              <Tooltip
                formatter={(value) => {
                  const count = Number(value);
                  return [`${count} project${count === 1 ? "" : "s"}`, ""];
                }}
                labelFormatter={(_, payload) =>
                  payload && payload[0] ? (payload[0].payload as { status: string }).status : ""
                }
                contentStyle={{ borderRadius: 6, fontSize: 12 }}
              />
              <Bar
                dataKey="count"
                radius={[4, 4, 0, 0]}
                onClick={(entry) => {
                  // recharts hands us a BarRectangleItem; the original datum sits on `.payload`.
                  const datum = (entry as { payload?: { status?: string } } | undefined)?.payload;
                  if (datum?.status) {
                    navigate(`/projects?status=${encodeURIComponent(datum.status)}`);
                  }
                }}
                cursor="pointer"
              >
                {data.map((row, index) => (
                  <Cell
                    key={row.status}
                    fill={index === data.length - 1 ? ACCENT : PRIMARY}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
